'use client';

import { fetchMeClient } from '@/lib/me-client';
import { nextOnboardingStep } from '@/lib/onboarding-step';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { BrandLogo } from './BrandLogo';
import { NotificationBell } from './NotificationBell';
import { ProfileMenu, type ProfileMenuDict } from './ProfileMenu';

/**
 * Client island for <TopBar>. The server-side fetch in <TopBar> seeds `me`
 * (or null) at request time — that handles the happy path with no flash.
 *
 * V2.9 defense: ALSO does a client-side /api/me check on mount. When the
 * server-side cookies() read silently fails (next-on-pages 1.13 layout
 * caching quirk), the server seed comes back null even for logged-in
 * users. The client recheck recovers from that without a hard refresh.
 * Cost: one /api/me call per page nav, returns 200 in ~50ms server-side.
 */
type Me = {
  user: {
    email: string;
    role: string;
    display_name: string | null;
    username: string | null;
  };
  // V3.4 (2026-06-03) — application_* surfaced from /me so the TopBar
  // can render the workspace tabs for pending applicants but route
  // clicks back to the right onboarding step.
  application?: {
    status: string;
    preferred_branch: string | null;
  } | null;
  // CRO-63 P0 #4 (2026-06-04) — release flags piped through so
  // production V1 hides 撰写 (Studio) + 发现 (Discover) tabs without
  // a separate /api/version round-trip. Default behaviour when
  // missing: show everything (staging / dev).
  flags?: {
    writing: boolean;
    discover: boolean;
  };
};

type Tab = {
  href: string;
  label: string;
  matches: (path: string) => boolean;
  // 2026-06-05 — flag-gated "coming soon" tab. Rendered as a disabled
  // link pointing to /coming-soon so the marketing surface stays
  // visible on production V1 where ENABLE_WRITING / ENABLE_DISCOVER
  // are off.
  comingSoon?: boolean;
};

type TopbarDict = {
  overview: string;
  vault: string;
  training: string;
  persona: string;
  studio: string;
  launch: string;
  discover: string;
  admin: string;
  login: string;
  signup: string;
  my_overview: string;
  share_settings: string;
  logout: string;
  locale_toggle_to: string;
  language_label: string;
  view_label: string;
  view_user: string;
  view_admin: string;
  theme_label: string;
  theme_soon: string;
};

/**
 * V3.4 (2026-06-02b) — seven-entry top nav. 发现 added back per Leon
 * 2026-06-02: public-twin discovery is useful for both consumers and
 * economists who want to browse / chat with other economists' twins.
 * Placed rightmost so the workflow tabs (concepts / training / soul /
 * studio / launch) read left-to-right as a single coherent journey
 * and 发现 sits as an "outside the journey" entry point.
 *
 *   概览 | 知识库 | 训练 | 分身设置 | 试聊 | 撰写 | 发现
 *
 * 2026-06-11 — Leon: 试聊 moved left of 撰写 so the natural flow
 * 训练 → 试聊 (smoke test analytic) → 撰写 (报告产出) reads in order.
 */
function buildTabs(dict: TopbarDict): { economist: Tab[]; admin: Tab } {
  return {
    economist: [
      { href: '/studio', label: dict.overview, matches: (p) => p === '/studio' },
      {
        href: '/studio/vault',
        label: dict.vault,
        matches: (p) => p.startsWith('/studio/vault'),
      },
      {
        href: '/studio/train',
        label: dict.training,
        matches: (p) => p === '/studio/train',
      },
      {
        href: '/studio/persona',
        label: dict.persona,
        matches: (p) => p.startsWith('/studio/persona') || p.startsWith('/studio/soul-tuning'),
      },
      {
        href: '/studio/launch',
        label: dict.launch,
        matches: (p) => p.startsWith('/studio/launch'),
      },
      {
        href: '/studio/studio',
        label: dict.studio,
        matches: (p) => p.startsWith('/studio/studio'),
      },
      {
        href: '/discover',
        label: dict.discover,
        matches: (p) => p.startsWith('/discover'),
      },
    ],
    admin: {
      href: '/platform',
      label: dict.admin,
      matches: (p) => p.startsWith('/platform'),
    },
  };
}

export function TopBarClient({
  me: initialMe,
  locale,
  dict,
}: {
  me: Me | null;
  locale: 'zh' | 'en';
  dict: TopbarDict;
}) {
  const pathname = usePathname() ?? '';
  const router = useRouter();
  const [me, setMe] = useState<Me | null>(initialMe);

  // V2.10i — always re-check /api/me on mount, regardless of the server
  // seed value. The previous "skip when initialMe truthy" gate left a hole:
  // when next-on-pages serves a stale layout bundle with me=null AND the
  // user already has a valid session, the client never rechecks and the
  // TopBar shows 登录/注册 over an authed page. One extra ~50ms /api/me
  // round-trip per nav is the right trade for that bug.
  // V3.4 (2026-06-03 P2-C) — fetchMeClient() shares the in-flight
  // promise with all ProfileMenu mounts on the same page, so the
  // first-paint /api/me fan-out collapses from 3-4 round-trips to 1.
  //
  // V3.4 (2026-06-04) — restore the "skip when initialMe truthy"
  // optimization, but only when the seed is non-null. The bug the
  // V2.10i comment refers to was server seed=null + valid session;
  // when seed is present we trust it and avoid the extra /api/me
  // round-trip on every nav. SW is SWR for /api/me anyway so the
  // recovery path stays cheap when needed.
  useEffect(() => {
    if (initialMe) return;
    let cancelled = false;
    fetchMeClient().then((body) => {
      if (cancelled) return;
      if (body) setMe(body as Me);
      else setMe(null);
    });
    return () => {
      cancelled = true;
    };
  }, [initialMe]);

  const role = me?.user.role ?? null;
  const tabsAll = buildTabs(dict);
  // V3.4 (2026-06-03) — pending users (mid-onboarding) see the workspace
  // tabs visually so they understand what's coming, but clicks bounce
  // back to the next onboarding step. The tabs render with opacity-50
  // when role==='pending' so the gate is visible without a tooltip.
  const isPending = role === 'pending';
  // CRO-63 P0 #4 (2026-06-04) — flags default to true so a stale /me
  // response (or pre-flag orchestrator) still shows the full nav.
  // Production V1 sets both to false on the orchestrator side.
  const flagWriting = me?.flags?.writing !== false;
  const flagDiscover = me?.flags?.discover !== false;
  // 2026-06-11 — Leon's call: drop 撰写 + 发现 entirely on prod when
  // the flags are off (was rendering them as disabled with 「敬请期待」
  // chips, which still looked like clickable nav entries). Staging keeps
  // them because both flags default to true there.
  const visibleEconTabs = tabsAll.economist.filter((t) => {
    if (!flagWriting && t.href === '/studio/studio') return false;
    if (!flagDiscover && t.href === '/discover') return false;
    return true;
  });
  const tabs =
    role === 'platform_admin'
      ? [...visibleEconTabs, tabsAll.admin]
      : role === 'economist'
        ? visibleEconTabs
        : // 2026-06-09 — general admin (Amy tier): same workspace nav as
          // economist so they don't lose all chrome the moment Leon grants
          // them admin. Tier separation is enforced at /platform entry,
          // not by hiding the workspace itself.
          role === 'admin'
          ? visibleEconTabs
          : isPending
            ? visibleEconTabs
            : role === 'consumer'
              ? // Consumers only see 发现; we keep showing it (with the
                // coming-soon chip if the flag is off) so the marketing
                // surface still tells them something is coming.
                visibleEconTabs.filter((t) => t.href === '/discover')
              : [];

  // For pending users the next-onboarding step is where every gated
  // click goes. We compute it once per render from /me data.
  const pendingRedirect = isPending
    ? (nextOnboardingStep(
        {
          role: me?.user.role ?? 'pending',
          display_name: me?.user.display_name ?? null,
          application_status: me?.application?.status ?? null,
          application_branch: me?.application?.preferred_branch ?? null,
        },
        pathname,
      ) ?? '/onboarding/role')
    : null;
  // V2.11 (2026-05-23) — TopBar is now mobile-friendly. Previously
  // `hidden md:flex` hid the entire top bar (including the brand logo)
  // on phones for every page outside /studio, which meant admins
  // couldn't get back to the landing page and had no clear chrome on
  // /platform / /discover / public twin pages. On /studio mobile we
  // still defer to WorkspaceMobileHeader (so we don't double-stack two
  // top bars there); everywhere else on mobile we render a compact
  // version: just the logo + ProfileMenu, no tabs.
  const inStudio = pathname.startsWith('/studio');
  // Leon ask (2026-06-28, #130) — /platform also mounts
  // WorkspaceMobileHeader via PlatformLayout; suppress the global
  // TopBar mobile bar there so admins don't see two stacked top bars.
  const inPlatform = pathname.startsWith('/platform');

  return (
    <>
      {/* Desktop bar. */}
      <div className="hidden md:flex h-[72px] bg-cream border-b border-soft px-14 items-center gap-12">
        <BrandMark />

        {tabs.length > 0 && (
          <nav className="flex gap-1 ml-2">
            {tabs.map((t) => {
              const active = t.matches(pathname);
              if (isPending) {
                return (
                  <button
                    key={t.href}
                    type="button"
                    onClick={() => router.replace(pendingRedirect ?? '/onboarding/role')}
                    aria-disabled
                    title="完成入驻后解锁"
                    className="relative px-4 py-2.5 text-sm rounded-md text-dusk/60 cursor-not-allowed opacity-60 hover:opacity-75"
                  >
                    {t.label}
                  </button>
                );
              }
              if (t.comingSoon) {
                return (
                  <Link
                    key={t.href}
                    href={`/coming-soon?from=${encodeURIComponent(t.href)}`}
                    className="relative px-4 py-2.5 text-sm rounded-md text-dusk/55 inline-flex items-center gap-1.5"
                    title="V1 暂未开放，敬请期待"
                  >
                    <span>{t.label}</span>
                    <span className="font-mono text-[10px] tracking-[0.16em] uppercase border border-dusk/30 text-dusk/60 px-1.5 py-0.5 rounded-r-xs">
                      敬请期待
                    </span>
                  </Link>
                );
              }
              return (
                <Link
                  key={t.href}
                  href={t.href}
                  // V3.4 (2026-06-04) — full prefetch (not just
                  // loading.tsx) so the next workspace tab's RSC
                  // payload is already in the cache when the user
                  // clicks. Cuts perceived nav latency from
                  // ~300-600ms (cold fetch) to whatever the React
                  // hydration + paint cost is (~50ms).
                  prefetch
                  className={`relative px-4 py-2.5 text-sm transition rounded-md ${
                    active
                      ? 'text-navy font-medium'
                      : 'text-dusk hover:text-navy hover:bg-surface-3'
                  }`}
                >
                  {t.label}
                  {active && <span className="absolute -bottom-5 left-4 right-4 h-0.5 bg-gold-3" />}
                </Link>
              );
            })}
          </nav>
        )}

        <div className="ml-auto flex items-center gap-3">
          {/* 2026-07-08 R95 wave 4 · notification bell — polls
              /me/notifications; drawer with recent 50 + unread badge.
              Only render when the user is logged in. */}
          {me?.user && <NotificationBell />}
          {/* V2.11 (2026-05-21g) — language toggle + admin/user switch
              + logout moved into the ProfileMenu dropdown. Same component
              powers the mobile chrome (compact variant). */}
          <ProfileMenu
            initialMe={me}
            locale={locale}
            dict={dict as ProfileMenuDict}
            variant="full"
          />
        </div>
      </div>

      {/* Mobile bar — non-studio + non-platform routes only.
          (Both /studio/* and /platform/* mount their own
          WorkspaceMobileHeader, so we'd otherwise double-stack.) */}
      {!inStudio && !inPlatform && (
        <div className="md:hidden sticky top-0 z-30 h-12 bg-cream/95 backdrop-blur border-b border-soft flex items-center px-4 pt-[env(safe-area-inset-top)]">
          <BrandLogo variant="horizontal" href="/" heightPx={24} />
          <div className="ml-auto flex items-center gap-2">
            {me?.user && <NotificationBell />}
            <ProfileMenu
              initialMe={me}
              locale={locale}
              dict={dict as ProfileMenuDict}
              variant="compact"
            />
          </div>
        </div>
      )}
    </>
  );
}

function BrandMark() {
  // V2.11 J4 — official horizontal logo (transparent PNG sits on the
  // cream topbar bg). 32px tall matches the previous CSS mark's
  // vertical footprint; aspect ratio handled inside BrandLogo.
  return <BrandLogo variant="horizontal" href="/" heightPx={32} />;
}
